import React, { useState } from 'react';
import {
  Check,
  Zap,
  Shield,
  ArrowRight,
  HelpCircle,
  Clock,
} from 'lucide-react';
import { PlanType, PLAN_LIMITS } from '../types';
import { updateOrganizationPlan } from '../services/api';

interface PricingSectionProps {
  domain: string;
  currentPlan: PlanType;
  usedCount: number;
  onPlanUpdated: (newPlan: PlanType) => void;
  onOpenContactModal?: (planId: PlanType | string) => void;
}

interface PlanDetails {
  id: PlanType;
  name: string;
  badge?: string;
  price: string;
  unitPrice?: string;
  period: string;
  billingCycle: string;
  description: string;
  limitLabel: string;
  limit: number;
  features: string[];
  ctaLabel: string;
  isPopular?: boolean;
}

export const PricingSection: React.FC<PricingSectionProps> = ({
  domain,
  currentPlan,
  usedCount,
  onPlanUpdated,
  onOpenContactModal,
}) => {
  const [isUpdating, setIsUpdating] = useState<PlanType | null>(null);
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const plans: PlanDetails[] = [
    {
      id: 'free',
      name: 'Solo',
      badge: 'Freemium',
      price: '0 PLN',
      period: 'na zawsze bez opłat',
      billingCycle: 'Bezpłatny start dla 1 konta',
      description: 'Dla freelancerów i administratorów testujących integrację z Google Workspace.',
      limitLabel: '1 skrzynka pocztowa',
      limit: 1,
      features: [
        '1 skrzynka pocztowa Gmail',
        'Podstawowe szablony HTML',
        'Wizualny edytor pól i kontaktów',
        'Dyskretna stopka CerebroSign w podpisie',
        'Bezpośrednie wstrzyknięcie do Gmail API',
        '100% prywatności (brak wglądu w maile)',
      ],
      ctaLabel: currentPlan === 'free' ? 'Aktywny pakiet' : 'Aktywuj plan Solo',
    },
    {
      id: 'team5',
      name: 'Team 5',
      price: '25 PLN',
      unitPrice: '5,00 PLN / użytkownik',
      period: 'netto / miesiąc',
      billingCycle: 'Rozliczenie miesięczne B2B',
      description: 'Idealny dla małych zespołów i startupów dbających o profesjonalny wizerunek.',
      limitLabel: 'Do 5 użytkowników domeny',
      limit: 5,
      features: [
        'Do 5 skrzynek Google Workspace',
        'Brak brandingu CerebroSign (White-label)',
        'Wszystkie szablony HTML (Outlook Safe)',
        'Edytor logotypów, awatarów i Social Media',
        'Klauzula RODO / poufności dla domeny',
        'Masowa synchronizacja z katalogu Google',
      ],
      ctaLabel: currentPlan === 'team5' ? 'Aktywny pakiet' : 'Wybierz Team 5',
    },
    {
      id: 'team10',
      name: 'Team 10',
      badge: 'Najchętniej wybierany',
      isPopular: true,
      price: '45 PLN',
      unitPrice: '4,50 PLN / użytkownik',
      period: 'netto / miesiąc',
      billingCycle: 'Rozliczenie miesięczne B2B',
      description: 'Dla rosnących firm poszukujących spójnego brandingu i marketingu w mailach.',
      limitLabel: 'Do 10 użytkowników domeny',
      limit: 10,
      features: [
        'Do 10 skrzynek Google Workspace',
        'Brak brandingu CerebroSign (White-label)',
        'Wszystkie szablony + paleta korporacyjna',
        'Rotacja banerów marketingowych w stopce',
        'Automatyczna synchronizacja katalogu Google',
        'Priorytetowe przetwarzanie wsadowe (batch)',
      ],
      ctaLabel: currentPlan === 'team10' ? 'Aktywny pakiet' : 'Wybierz Team 10',
    },
    {
      id: 'business25',
      name: 'Business 25',
      badge: 'Rekomendowany B2B',
      price: '89 PLN',
      unitPrice: '3,56 PLN / użytkownik',
      period: 'netto / miesiąc',
      billingCycle: 'Rozliczenie miesięczne B2B',
      description: 'Dla średnich organizacji, agencji i software house’ów z wieloma działami.',
      limitLabel: 'Do 25 użytkowników domeny',
      limit: 25,
      features: [
        'Do 25 skrzynek Google Workspace',
        'Generator linków i tagowanie UTM dla banerów',
        'Zarządzanie grupami i działami firmy',
        'Dedykowane szablony i reguły prawne per dział',
        'Centralna wymuszona synchronizacja podpisów',
        'Priorytetowe wsparcie SLA dla IT',
      ],
      ctaLabel: currentPlan === 'business25' ? 'Aktywny pakiet' : 'Wybierz Business 25',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      badge: 'Korporacje',
      price: '3.50 PLN',
      unitPrice: 'od 3,50 PLN / skrzynka',
      period: 'netto / użytkownik / mc',
      billingCycle: 'Faktura VAT z odroczonym terminem',
      description: 'Dla korporacji powyżej 25 kont wymagających dedykowanego wdrożenia i SLA.',
      limitLabel: 'Powyżej 25 kont (brak limitu)',
      limit: 99999,
      features: [
        'Nielimitowana liczba skrzynek w domenie',
        'Dedykowany szablon HTML kodowany na zamówienie',
        'Wsparcie integracji Google Workspace Admin API',
        'Dedykowany Cloud Security Architect',
        'Gwarantowane SLA 99.9% oraz umowa powierzenia RODO',
        'Dedykowany opiekun konta i fakturowanie zbiorcze',
      ],
      ctaLabel: currentPlan === 'enterprise' ? 'Aktywny pakiet' : 'Zapytaj o Enterprise',
    },
  ];

  const currentLimit = PLAN_LIMITS[currentPlan] || 1;
  const usagePercentage = currentPlan === 'enterprise'
    ? Math.min(100, Math.round((usedCount / 50) * 100))
    : Math.min(100, Math.round((usedCount / currentLimit) * 100));

  const handleSelectPlan = async (planId: PlanType) => {
    if (planId === currentPlan) return;

    // For paid plans, redirect user to the contact & plan upgrade form!
    if (planId !== 'free') {
      if (onOpenContactModal) {
        onOpenContactModal(planId);
      }
      return;
    }

    // Downgrade to Free plan
    setIsUpdating(planId);
    setUpdateMessage(null);

    try {
      await updateOrganizationPlan(domain, planId);
      onPlanUpdated(planId);
      setUpdateMessage({
        type: 'success',
        text: `Pakiet domeny ${domain} został pomyślnie przestawiony na: Solo (Free)!`,
      });
    } catch (err: any) {
      console.error('Plan update failed:', err);
      setUpdateMessage({
        type: 'error',
        text: err?.message || 'Nie udało się zaktualizować pakietu. Spróbuj ponownie.',
      });
    } finally {
      setIsUpdating(null);
    }
  };

  return (
    <section
      id="pricing-section"
      aria-labelledby="pricing-section-title"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        maxWidth: 1360,
        margin: '0 auto',
        width: '100%',
        fontFamily: 'var(--font-body)',
      }}
    >
      {/* Header Banner - Enterprise Clean Minimalist Style */}
      <header
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '28px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: '0 4px 20px -2px rgba(11, 25, 44, 0.05)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0066FF', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              <Zap size={15} />
              <span>Przejrzysty model subskrypcyjny B2B</span>
            </div>
            <h2
              id="pricing-section-title"
              style={{
                fontSize: 'clamp(22px, 3vw, 30px)',
                fontWeight: 700,
                color: '#0B192C',
                margin: 0,
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.02em',
              }}
            >
              Pakiety CerebroSign dla Twojej domeny ({domain})
            </h2>
            <p style={{ color: '#475569', fontSize: 14, marginTop: 8, marginBottom: 0, maxWidth: 680, lineHeight: 1.6 }}>
              Płać wyłącznie za aktywne skrzynki pracownicze. Każdy plan obejmuje nielimitowane wdrożenia
              bezpiecznych, tabelarycznych stopek HTML bez ingerencji w prywatność wiadomości.
            </p>
          </div>

          {/* Current Quota Box - Corporate Metric Block */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '16px 20px',
              minWidth: 270,
            }}
          >
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
              <span>Wykorzystany limit domeny:</span>
              <strong style={{ color: '#0066FF', fontFamily: 'var(--font-body)', fontWeight: 700 }}>
                {usedCount} / {currentPlan === 'enterprise' ? '∞' : currentLimit}
              </strong>
            </div>

            {/* Progress bar */}
            <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden', margin: '8px 0' }}>
              <div
                style={{
                  width: `${usagePercentage}%`,
                  height: '100%',
                  background: usagePercentage >= 100 ? '#ef4444' : '#0066FF',
                  borderRadius: 999,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>

            <div style={{ fontSize: 11.5, color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
              <span>Aktywna subskrypcja:</span>
              <strong style={{ color: '#0B192C' }}>{plans.find((p) => p.id === currentPlan)?.name}</strong>
            </div>
          </div>
        </div>

        {updateMessage && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 8,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: updateMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
              border: `1px solid ${updateMessage.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
              color: updateMessage.type === 'success' ? '#059669' : '#dc2626',
            }}
          >
            {updateMessage.type === 'success' ? <Check size={16} /> : <HelpCircle size={16} />}
            <span>{updateMessage.text}</span>
          </div>
        )}
      </header>

      {/* Pricing Cards Grid - Corporate SaaS Tier Cards */}
      <div
        role="region"
        aria-label="Pakiety cennika CerebroSign"
        className="pricing-cards-grid"
      >
        {plans.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          const isBusy = isUpdating === plan.id;
          const isFeatured = plan.isPopular;

          return (
            <article
              key={plan.id}
              aria-label={`Pakiet ${plan.name} – ${plan.price}`}
              className={`pricing-corporate-card ${isFeatured ? 'featured' : ''} ${isCurrent ? 'current' : ''}`}
            >
              {plan.badge && (
                <div
                  style={{
                    position: 'absolute',
                    top: -11,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: isFeatured ? '#0066FF' : '#0B192C',
                    color: '#ffffff',
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '3px 12px',
                    borderRadius: 999,
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {plan.badge}
                </div>
              )}

              {/* Card Top: Plan Name & Quota */}
              <div>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <h3
                    style={{
                      fontSize: 19,
                      fontWeight: 700,
                      color: '#0B192C',
                      margin: 0,
                      fontFamily: 'var(--font-display)',
                    }}
                  >
                    {plan.name}
                  </h3>
                  {isCurrent && (
                    <span className="micro-tag micro-tag-info">
                      Aktywny
                    </span>
                  )}
                </header>

                <p style={{ fontSize: 12.5, color: '#475569', minHeight: 36, margin: '4px 0 16px', lineHeight: 1.45 }}>
                  {plan.description}
                </p>

                {/* Price Display with clear monthly billing breakdown */}
                <div
                  style={{
                    marginBottom: 18,
                    borderTop: '1px solid #e2e8f0',
                    borderBottom: '1px solid #e2e8f0',
                    padding: '14px 0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 32,
                        fontWeight: 800,
                        color: '#0B192C',
                        fontFamily: 'var(--font-body)',
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {plan.price}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                      {plan.period}
                    </span>
                    {plan.unitPrice && (
                      <span style={{ fontSize: 11, color: '#0066FF', fontWeight: 600 }}>
                        {plan.unitPrice}
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: isCurrent ? '#0066FF' : '#334155',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      padding: '4px 8px',
                      borderRadius: 6,
                      display: 'inline-block',
                    }}
                  >
                    {plan.limitLabel}
                  </div>
                </div>

                {/* Features list */}
                <ul
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    marginBottom: 24,
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {plan.features.map((feat, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        fontSize: 12.5,
                        color: '#334155',
                        lineHeight: 1.4,
                      }}
                    >
                      <Check
                        size={15}
                        color={isFeatured ? '#0066FF' : '#059669'}
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Button - Elegant Corporate Checkout */}
              <div>
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={isCurrent || isBusy}
                  className={`pricing-btn-checkout ${isFeatured ? 'pricing-btn-primary' : 'pricing-btn-corporate'}`}
                  style={{
                    opacity: isCurrent ? 0.65 : 1,
                    cursor: isCurrent ? 'default' : 'pointer',
                  }}
                >
                  {isBusy ? (
                    <span>Przetwarzanie...</span>
                  ) : (
                    <>
                      <span>{plan.ctaLabel}</span>
                      {!isCurrent && <ArrowRight size={14} />}
                    </>
                  )}
                </button>

                <div style={{ textAlign: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {plan.billingCycle}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Corporate Security & Terms Footer */}
      <aside
        aria-label="Informacje o bezpieczeństwie i licencjonowaniu"
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '20px 24px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
          boxShadow: '0 4px 16px rgba(11, 25, 44, 0.04)',
        }}
      >
        <article style={{ display: 'flex', gap: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0066FF',
              flexShrink: 0,
            }}
          >
            <Shield size={19} />
          </div>
          <div>
            <h4 style={{ fontSize: 13.5, fontWeight: 700, color: '#0B192C', marginBottom: 4, fontFamily: 'var(--font-display)' }}>
              Certyfikowane Bezpieczeństwo i RODO
            </h4>
            <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.5 }}>
              CerebroSign posiada uprawnienia wyłącznie do modyfikacji pola podpisu. Gwarantujemy 100% prywatności oraz całkowity brak dostępu do treści wiadomości i załączników.
            </p>
          </div>
        </article>

        <article style={{ display: 'flex', gap: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669',
              flexShrink: 0,
            }}
          >
            <Clock size={19} />
          </div>
          <div>
            <h4 style={{ fontSize: 13.5, fontWeight: 700, color: '#0B192C', marginBottom: 4, fontFamily: 'var(--font-display)' }}>
              Fakturowanie B2B &amp; Elastyczność
            </h4>
            <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.5 }}>
              Faktury VAT 23% wystawiane automatycznie co miesiąc. Możesz zwiększać liczbę kont w dowolnym momencie trwania subskrypcji.
            </p>
          </div>
        </article>
      </aside>
    </section>
  );
};
export default PricingSection;
